// frontend/src/hooks/useDiscardPrehydrationCheck.js
//
// A checkbox in a prerendered snapshot shows exactly the state React holds,
// from the moment React attaches - backlog #464. The counterpart of
// useAdoptPrehydrationValue.js, for a box whose tick must NOT be adopted.
//
// THE PROBLEM. React does not touch an input's `checked` when it HYDRATES it,
// so a box ticked in the snapshot before React attached stays ticked in the
// DOM while the component's state says unticked. For the sign-up terms box
// that tick is not consent: the browser's own form restoration (back/forward,
// a reload) re-ticks a box nobody touched on this visit, and a pre-hydration
// tap is unrecorded anyway. Adopting it would open the terms gate on a tick
// the visitor may never have given. Leaving it would let the DOM say ticked
// while the gate is closed - and the visitor's next tap would UNtick it.
//
// THE RULE. In the commit of the hydration pass, set the DOM's `checked` to the
// state value. The visitor sees the box as the gate sees it, and ticks it,
// with React attached, to agree. Never on a client-only boot (a route change,
// the popup): React renders the box from state there.
//
// React writes `checked` again whenever it next commits the box, so a page
// that happens to re-render the box after hydration would heal on its own -
// but only then. This does not depend on it: a memoised box, or a page that
// stops re-rendering after hydration, still never shows a tick the gate does
// not hold. Setting the property directly is what React itself does, and its
// change tracking follows it, so the visitor's next tap is a normal onChange.

import { useLayoutEffect } from "react";
import useHydrated from "../motion/useHydrated";

/**
 * @param {{current: (HTMLInputElement|null)}} ref - the checkbox's input
 *   element (an MUI Checkbox's `inputRef`). A null ref is skipped.
 * @param {boolean} checked - the state value the box rendered with.
 */
export default function useDiscardPrehydrationCheck(ref, checked) {
  const hydrated = useHydrated();

  useLayoutEffect(() => {
    // `hydrated` is false only in the commit of the hydration pass.
    if (hydrated) return;
    const el = ref.current;
    if (el && el.checked !== Boolean(checked)) el.checked = Boolean(checked);
  }, [hydrated, ref, checked]);
}

export { useDiscardPrehydrationCheck };
